import { supabase, supabaseFetch } from '../../../lib/supabase';

/**
 * Process and compress image to multiple sizes
 * @param {File} imageFile - Original image file
 * @param {Object} cropArea - Crop coordinates from react-easy-crop
 * @param {string} aspectRatio - '4:3' or 'square'
 * @returns {Object} { displayBlob, thumbnailBlob, fileName }
 */
export async function processImage(imageFile, cropArea, aspectRatio = '4:3') {
  // 1. Load image into canvas
  const image = await createImage(imageFile);

  // 2. Crop to selected area
  const croppedCanvas = getCroppedCanvas(image, cropArea);

  // 3. Generate display version
  const displayCanvas = resizeCanvas(croppedCanvas, {
    maxWidth: aspectRatio === 'square' ? 800 : 900,
    maxHeight: aspectRatio === 'square' ? 800 : 675,
    quality: 0.75
  });

  // 4. Generate thumbnail (always square — centre-crop then resize)
  const thumbnailCanvas = createSquareThumbnail(croppedCanvas, 300);

  // 5. Convert to WebP blobs
  const displayBlob = await canvasToBlob(displayCanvas, 'image/webp', 0.75);
  const thumbnailBlob = await canvasToBlob(thumbnailCanvas, 'image/webp', 0.85);

  // 6. Generate filename
  const timestamp = Date.now();
  const fileName = `${timestamp}-${Math.random().toString(36).substring(2, 11)}`;

  return {
    displayBlob,
    thumbnailBlob,
    fileName,
    displaySize: displayBlob.size,
    thumbnailSize: thumbnailBlob.size
  };
}

/**
 * Create image element from file
 * @param {File} file - Image file
 * @returns {Promise<HTMLImageElement>}
 */
function createImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (err) => reject(new Error('Failed to load image: ' + (err.message || 'Unknown error')));
      img.src = e.target.result;
    };
    reader.onerror = (err) => reject(new Error('Failed to read file: ' + (err.message || 'Unknown error')));
    reader.readAsDataURL(file);
  });
}

/**
 * Crop image to selected area.
 * Handles out-of-bounds crop coordinates (when user zooms out so image is
 * smaller than the crop frame) by filling empty space with white and
 * centering the visible portion.
 * @param {HTMLImageElement} image - Source image
 * @param {Object} cropArea - Crop coordinates from react-easy-crop
 * @returns {HTMLCanvasElement}
 */
function getCroppedCanvas(image, cropArea) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = cropArea.width;
  canvas.height = cropArea.height;

  // Fill with white background (visible when crop extends beyond image bounds)
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Calculate the overlap between crop area and actual image bounds
  const srcX = Math.max(0, cropArea.x);
  const srcY = Math.max(0, cropArea.y);
  const srcRight = Math.min(image.naturalWidth, cropArea.x + cropArea.width);
  const srcBottom = Math.min(image.naturalHeight, cropArea.y + cropArea.height);
  const srcW = srcRight - srcX;
  const srcH = srcBottom - srcY;

  // Where to draw on the canvas (offset if crop starts before image)
  const destX = srcX - cropArea.x;
  const destY = srcY - cropArea.y;

  if (srcW > 0 && srcH > 0) {
    ctx.drawImage(
      image,
      srcX, srcY, srcW, srcH,
      destX, destY, srcW, srcH
    );
  }

  return canvas;
}

/**
 * Resize canvas maintaining aspect ratio
 * @param {HTMLCanvasElement} sourceCanvas - Source canvas
 * @param {Object} options - Resize options
 * @returns {HTMLCanvasElement}
 */
function resizeCanvas(sourceCanvas, { maxWidth, maxHeight, quality }) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  let width = sourceCanvas.width;
  let height = sourceCanvas.height;

  // Scale down if needed
  if (width > maxWidth || height > maxHeight) {
    const ratio = Math.min(maxWidth / width, maxHeight / height);
    width = width * ratio;
    height = height * ratio;
  }

  canvas.width = width;
  canvas.height = height;

  ctx.drawImage(sourceCanvas, 0, 0, width, height);

  return canvas;
}

/**
 * Create a square thumbnail by centre-cropping then resizing.
 * For a 4:3 source, this extracts the centre square (removing side strips).
 * @param {HTMLCanvasElement} sourceCanvas - Source canvas (cropped image)
 * @param {number} size - Output square size in pixels
 * @returns {HTMLCanvasElement}
 */
function createSquareThumbnail(sourceCanvas, size) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const srcWidth = sourceCanvas.width;
  const srcHeight = sourceCanvas.height;

  // Determine the square crop region (centre of source)
  const cropSize = Math.min(srcWidth, srcHeight);
  const offsetX = (srcWidth - cropSize) / 2;
  const offsetY = (srcHeight - cropSize) / 2;

  canvas.width = size;
  canvas.height = size;

  ctx.drawImage(
    sourceCanvas,
    offsetX, offsetY, cropSize, cropSize,
    0, 0, size, size
  );

  return canvas;
}

/**
 * Convert canvas to blob
 * @param {HTMLCanvasElement} canvas - Canvas to convert
 * @param {string} mimeType - Output format
 * @param {number} quality - Compression quality (0–1)
 * @returns {Promise<Blob>}
 */
function canvasToBlob(canvas, mimeType = 'image/webp', quality = 0.85) {
  return new Promise((resolve, reject) => {
    // Set a timeout to prevent hanging if toBlob never calls back
    const timeout = setTimeout(() => {
      reject(new Error('Canvas toBlob timed out - WebP may not be supported'));
    }, 10000);

    try {
      canvas.toBlob((blob) => {
        clearTimeout(timeout);
        if (blob) {
          resolve(blob);
        } else {
          // WebP might not be supported, fall back to JPEG
          console.warn('WebP not supported, falling back to JPEG');
          canvas.toBlob((jpegBlob) => {
            if (jpegBlob) {
              resolve(jpegBlob);
            } else {
              reject(new Error('Failed to convert canvas to blob'));
            }
          }, 'image/jpeg', quality);
        }
      }, mimeType, quality);
    } catch (err) {
      clearTimeout(timeout);
      reject(new Error('Failed to convert canvas to blob: ' + err.message));
    }
  });
}

/**
 * Upload processed images to Supabase Storage
 * @param {Blob} displayBlob - Display version
 * @param {Blob} thumbnailBlob - Thumbnail version
 * @param {string} fileName - Base filename
 * @param {string} bucket - 'hero-images' or 'inline-images'
 * @param {string} userId - Current user ID
 * @returns {Object} { displayUrl, thumbnailUrl }
 */
export async function uploadToSupabase(displayBlob, thumbnailBlob, fileName, bucket, userId) {
  // Always use image/webp unless the JPEG fallback was triggered
  const isJpegFallback = displayBlob.type === 'image/jpeg';
  const contentType = isJpegFallback ? 'image/jpeg' : 'image/webp';
  const fileExt = isJpegFallback ? 'jpg' : 'webp';

  // Upload display version
  const displayPath = `public/${userId}/${fileName}-display.${fileExt}`;
  console.log('Uploading display image to:', bucket, displayPath);

  const { error: displayError } = await supabaseFetch.storageUpload(bucket, displayPath, displayBlob, { contentType });

  if (displayError) {
    console.error('Display upload error:', displayError);
    throw new Error('Failed to upload display image: ' + (displayError.message || 'Unknown error'));
  }

  // Upload thumbnail version
  const thumbnailPath = `public/${userId}/${fileName}-thumb.${fileExt}`;
  console.log('Uploading thumbnail image to:', bucket, thumbnailPath);

  const { error: thumbnailError } = await supabaseFetch.storageUpload(bucket, thumbnailPath, thumbnailBlob, { contentType });

  if (thumbnailError) {
    console.error('Thumbnail upload error:', thumbnailError);
    throw new Error('Failed to upload thumbnail: ' + (thumbnailError.message || 'Unknown error'));
  }

  // Get public URLs (getPublicUrl is synchronous - safe to keep)
  const { data: displayPublicData } = supabase.storage
    .from(bucket)
    .getPublicUrl(displayPath);

  const { data: thumbnailPublicData } = supabase.storage
    .from(bucket)
    .getPublicUrl(thumbnailPath);

  console.log('Upload complete. Display URL:', displayPublicData.publicUrl);

  return {
    displayUrl: displayPublicData.publicUrl,
    thumbnailUrl: thumbnailPublicData.publicUrl
  };
}
